import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { isNativeApp } from "../services/capacitor";
import AuthShell, { AuthField, StoreBadges } from "../components/auth/AuthShell";
import { GameButton, GameIcon } from "../components/game";

interface SetupInfo {
  user: {
    id: string;
    email: string;
    full_name: string | null;
    phone: string | null;
  };
  role: string | null;
  club: {
    id: string;
    name: string;
    logo_url: string | null;
  } | null;
}

export default function SetupAccount() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { setAuth } = useAuth();

  const [setupInfo, setSetupInfo] = useState<SetupInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    full_name: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t("setup.invalidLink", "Link de activare invalid"));
      setLoading(false);
      return;
    }
    verifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function verifyToken() {
    if (!token) return;
    try {
      setLoading(true);
      const response = await api.get(`/auth/verify-setup-token/${token}`);
      setSetupInfo(response.data);
      // Pre-fill name and phone if available
      if (response.data.user.full_name || response.data.user.phone) {
        setFormData((prev) => ({
          ...prev,
          full_name: response.data.user.full_name || "",
          phone: response.data.user.phone || "",
        }));
      }
      setError(null);
    } catch (err: any) {
      console.error("Failed to verify setup token:", err);
      if (err?.response?.status === 400) {
        setError(t("setup.invalidOrExpired", "Linkul a expirat sau a fost deja folosit. Dacă nu ți-ai activat încă contul, cere un link nou de pe pagina de autentificare."));
      } else {
        setError(t("setup.verificationFailed", "Verificarea link-ului a eșuat"));
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormErr(null);

    if (formData.password !== formData.confirmPassword) {
      setFormErr(t("setup.passwordsNotMatch", "Parolele nu coincid"));
      return;
    }
    if (formData.password.length < 6) {
      setFormErr(t("setup.passwordTooShort", "Parola trebuie să aibă cel puțin 6 caractere"));
      return;
    }
    if (!token) return;

    setSubmitting(true);
    try {
      const response = await api.post("/auth/complete-setup", {
        token,
        password: formData.password,
        full_name: formData.full_name.trim() || undefined,
        phone: formData.phone.trim() || undefined,
      });

      // Log the user in, then show the "download the app" success screen
      // (no auto-redirect — we want them to see the store links).
      await setAuth(
        {
          id: response.data.user.id,
          email: response.data.user.email,
          name: response.data.user.name,
          full_name: response.data.user.name,
        },
        response.data.token
      );
      setSuccess(true);
    } catch (err: any) {
      console.error("Failed to complete setup:", err);
      const serverMsg: string | undefined = err?.response?.data?.error;
      setFormErr(
        err?.response?.status === 400 && /invalid or expired/i.test(serverMsg || "")
          ? t("setup.invalidOrExpired", "Linkul a expirat sau a fost deja folosit. Dacă nu ți-ai activat încă contul, cere un link nou de pe pagina de autentificare.")
          : serverMsg || t("setup.setupFailed", "Activarea contului a eșuat"),
      );
      setSubmitting(false);
    }
  }

  // ── Loading ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <AuthShell>
        <div className="flex items-center justify-center gap-3 py-4">
          <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-slate-600 font-medium">{t("common.loading", "Se încarcă...")}</span>
        </div>
      </AuthShell>
    );
  }

  // ── Invalid / expired link ─────────────────────────────────────────
  if (error) {
    return (
      <AuthShell
        icon={<GameIcon name="bolt" className="w-8 h-8 text-rose-500" />}
        title={t("setup.invalidTitle", "Link invalid")}
      >
        <p className="text-center text-slate-600 text-sm mb-5">{error}</p>
        <GameButton onClick={() => navigate("/login")}>{t("setup.goToLogin", "Mergi la autentificare")}</GameButton>
      </AuthShell>
    );
  }

  if (!setupInfo) return null;

  // ── Success — download the app + continue ──────────────────────────
  if (success) {
    return (
      <AuthShell
        icon={<GameIcon name="check" className="w-8 h-8 text-emerald-600" />}
        title={t("setup.welcomeSuccess", "Bine ai venit!")}
        subtitle={t(
          "setup.accountActivatedApp",
          "Contul tău e activat. Descarcă aplicația și intră cu parola pe care tocmai ai ales-o."
        )}
      >
        {!isNativeApp() && (
          <div className="mb-5">
            <StoreBadges />
          </div>
        )}
        <GameButton variant="ghost" onClick={() => navigate("/dashboard")}>
          {t("setup.continueWeb", "Continuă în versiunea web")}
        </GameButton>
      </AuthShell>
    );
  }

  // ── Main setup form ────────────────────────────────────────────────
  const roleLabels: Record<string, string> = {
    platform_admin: t("roles.platform_admin", "Administrator platformă"),
    club_admin: t("roles.club_admin", "Administrator club"),
    coach: t("roles.coach", "Antrenor"),
    athlete: t("roles.athlete", "Sportiv"),
    parent: t("roles.parent", "Părinte"),
  };
  const roleBadgeClasses: Record<string, string> = {
    platform_admin: "bg-indigo-100 text-indigo-700",
    club_admin: "bg-blue-100 text-blue-700",
    coach: "bg-purple-100 text-purple-700",
    athlete: "bg-emerald-100 text-emerald-700",
    parent: "bg-amber-100 text-amber-700",
  };
  const roleLabel = setupInfo.role ? roleLabels[setupInfo.role] || setupInfo.role : null;
  const roleBadgeClass = (setupInfo.role && roleBadgeClasses[setupInfo.role]) || "bg-slate-100 text-slate-700";

  return (
    <AuthShell
      icon={<GameIcon name="star" className="w-8 h-8" />}
      title={t("setup.activateAccount", "Activează-ți contul")}
      subtitle={
        setupInfo.club
          ? t("setup.welcomeToClub", "Bine ai venit la {{clubName}}!", { clubName: setupInfo.club.name })
          : t("setup.welcomeToPlatform", "Bine ai venit în iGROWth!")
      }
    >
      {/* Account info */}
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 mb-5 space-y-2.5">
        <div className="flex justify-between items-center gap-3">
          <span className="text-xs text-slate-500">{t("setup.email", "Email")}</span>
          <span className="text-sm font-semibold text-slate-800 truncate">{setupInfo.user.email}</span>
        </div>
        {roleLabel && (
          <div className="flex justify-between items-center gap-3">
            <span className="text-xs text-slate-500">{t("setup.role", "Rol")}</span>
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${roleBadgeClass}`}>{roleLabel}</span>
          </div>
        )}
        {setupInfo.club && (
          <div className="flex justify-between items-center gap-3">
            <span className="text-xs text-slate-500">{t("setup.club", "Club")}</span>
            <span className="text-sm font-semibold text-slate-800 truncate">{setupInfo.club.name}</span>
          </div>
        )}
      </div>

      {formErr && (
        <div className="p-3 mb-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl">{formErr}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <AuthField
          label={t("setup.fullName", "Nume complet")}
          type="text"
          value={formData.full_name}
          onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
          placeholder={t("setup.fullNamePlaceholder", "Introdu numele tău complet") || ""}
        />
        <AuthField
          label={t("setup.phone", "Telefon")}
          type="tel"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          placeholder={t("setup.phonePlaceholder", "0712 345 678") || ""}
        />
        <div>
          <AuthField
            label={`${t("setup.password", "Parolă")} *`}
            type="password"
            required
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            placeholder={t("setup.passwordPlaceholder", "Alege o parolă sigură") || ""}
          />
          <p className="text-xs text-slate-400 mt-1">{t("setup.passwordHint", "Minimum 6 caractere")}</p>
        </div>
        <AuthField
          label={`${t("setup.confirmPassword", "Confirmă parola")} *`}
          type="password"
          required
          value={formData.confirmPassword}
          onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
          placeholder={t("setup.confirmPasswordPlaceholder", "Re-introdu parola") || ""}
        />

        <GameButton type="submit" variant="mint" disabled={submitting}>
          {submitting ? t("setup.activating", "Se activează...") : t("setup.activateButton", "Activează contul")}
        </GameButton>
      </form>

      <p className="text-center text-xs text-slate-400 mt-5">
        {t("setup.securityNote", "Datele tale sunt securizate și nu vor fi partajate cu terți.")}
      </p>
    </AuthShell>
  );
}
